import React from 'react';
import PropTypes from 'prop-types';
import BotIcon from '../../assets/icons/BotIcon';
import Colors from '../../utils/Colors';
import config from '../../config';
import PersonIcon from "../../assets/icons/PersonIcon";

const getAvatarSize = (variant, size) => {
    if (size) return size;
    switch (variant) {
        case 'chat':
            return 45;
        default:
            return 30;
    }
};

const safeString = (value) => (typeof value === 'string' ? value : '');

/*
 * Which circle colour someone gets when they have no photo: the letter decides
 * it, and nothing else.
 *
 * It was hashed from firstName + lastName + email, and that was wrong - the
 * screens do not all hand over the same contact. One has the e-mail, another
 * only a first name, so the same person came out pink in the header and yellow
 * in the chat. The letter shown in the circle is the one thing every caller
 * agrees on, so the colour is tied to that: whatever you can see, you can
 * predict.
 *
 * The cost is that two people whose names begin alike share a colour. That is
 * the better trade - a colour that is the same everywhere is worth having, one
 * that moves between screens is worse than none.
 */
const pickAvatarColor = (letter) => {
    const code = letter ? letter.toUpperCase().charCodeAt(0) : 0;
    return Colors.avatarPalette[code % Colors.avatarPalette.length];
};

const ContactAvatar = ({contact, variant = 'default', size, color = Colors.blueDark}) => {
    if (!contact) return null;

    const {
        platform = config.platformsMap.company,
        firstName,
        lastName,
        email,
        thumbUrl,
        bot,
    } = contact;

    const safeFirst = safeString(firstName);
    const safeLast = safeString(lastName);
    const safeEmail = safeString(email);

    const avatarSize = getAvatarSize(variant, size);
    const initials =
        safeFirst || safeLast
            ? `${safeFirst.charAt(0)}${safeLast.charAt(0)}`.toUpperCase()
            : safeEmail
                ? safeEmail.charAt(0).toUpperCase()
                : '?';

    const thumbIsPresent = Boolean(thumbUrl);
    const isBot = Boolean(bot);
    const isChatAvatar = variant === 'chat' || variant === 'chatMenu';
    const showingDefaultProfile =
        !thumbIsPresent && !isBot && platform !== config.platformsMap.company;

    /*
     * A bot gets the brand, every time - it is one thing, not one of many
     * people, so there is nothing to tell apart and nothing to derive a colour
     * from. Without this it fell into the transparent branch and its white mark
     * was drawn on nothing at all.
     */
    let background = 'transparent';
    if (isBot) {
        background = isChatAvatar ? 'var(--ds-bg-tertiary)' : 'var(--ds-btn-brand)';
    } else if (showingDefaultProfile && isChatAvatar) {
        background = 'var(--ds-bg-tertiary)';
    } else if (!thumbIsPresent && !showingDefaultProfile) {
        background = pickAvatarColor(initials.charAt(0));
    }

    const containerStyle = {
        width: avatarSize,
        height: avatarSize,
        borderRadius: '50%',
        boxSizing: 'border-box',
        border: isChatAvatar ? '1px solid var(--ds-border-secondary)' : 'none',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: background,
        color: isChatAvatar && isBot ? 'var(--ds-fg-tertiary)' : 'var(--ds-text-on-solid)',
        fontSize: avatarSize / 2.5,
        fontWeight: 600,
        userSelect: 'none',
    };

    const imageStyle = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '50%',
    };

    let content = null;

    if (bot) {
        /*
         * A bot, not a person. It was a smiling-face bitmap that read as one
         * more visitor in a column of visitors; the drawn mark says what it is,
         * and being SVG it takes the circle's own colour and stays sharp.
         *
         * No longer limited to variant 'chat' - a bot is a bot wherever its
         * avatar is drawn, and everywhere else it was falling through to the
         * initials of a name it does not have.
         */
        content = (
            <BotIcon
                width={avatarSize * 0.6}
                height={avatarSize * 0.6}
                strokeWidth={1.6}
                stroke="currentColor"
            />
        );
    } else if (thumbIsPresent) {
        content = <img src={thumbUrl} style={imageStyle} alt="avatar"/>;
    } else if (platform === config.platformsMap.company) {
        content = initials;
    } else {
        content = (
            <PersonIcon
                style={{color: isChatAvatar ? 'var(--ds-fg-tertiary)' : color}}
                width={avatarSize * 0.6}
                height={avatarSize * 0.6}
            />
        )
    }

    return <div style={containerStyle}>{content}</div>;
};

ContactAvatar.propTypes = {
    contact: PropTypes.object.isRequired,
    variant: PropTypes.oneOf(['chat', 'menu', 'chatMenu', 'default']),
    size: PropTypes.number,
};

export default ContactAvatar;



// WEBPACK FOOTER //
// ./src/components-new/general/ContactAvatar.jsx